import { isNative } from "@/lib/capacitor";

const BIOMETRIC_SERVER = "transportmate.app";
const BIOMETRIC_OPT_IN_KEY = "tm_biometric_enabled";
const BIOMETRIC_AUTO_SUPPRESS_KEY = "tm_suppress_biometric_auto";

let biometricPlugin: any = null;
let biometricPluginLoaded = false;

function getCachedBiometricPlugin() {
  return biometricPlugin;
}

/**
 * Load the NativeBiometric plugin only on native platforms.
 * IMPORTANT: never return the Capacitor plugin proxy directly from an async function,
 * because the proxy exposes a .then() method and JS will treat it as a thenable.
 */
async function ensureBiometricPluginLoaded(): Promise<boolean> {
  if (!isNative) {
    console.log("[Biometric] Not native platform, skipping plugin load");
    return false;
  }

  if (biometricPluginLoaded) {
    return biometricPlugin !== null;
  }

  try {
    console.log("[Biometric] Importing @capgo/capacitor-native-biometric...");
    const mod = await import("@capgo/capacitor-native-biometric");
    biometricPlugin = mod.NativeBiometric;
    biometricPluginLoaded = true;
    console.log("[Biometric] Plugin imported successfully");
    return true;
  } catch (e) {
    console.warn("[Biometric] Plugin not available:", e);
    biometricPlugin = null;
    biometricPluginLoaded = true;
    return false;
  }
}

/**
 * Check if biometric auth (Face ID / Touch ID / Fingerprint) is available on this device.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  console.log("[Biometric] Checking availability... isNative:", isNative);
  if (!isNative) {
    console.log("[Biometric] Not native, returning false");
    return false;
  }

  const loaded = await ensureBiometricPluginLoaded();
  const plugin = loaded ? getCachedBiometricPlugin() : null;
  if (!plugin) {
    console.log("[Biometric] Plugin not available (not native or import failed)");
    return false;
  }

  try {
    console.log("[Biometric] Calling plugin.isAvailable()...");
    const result = await Promise.race([
      plugin.isAvailable(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Biometric isAvailable timed out after 5s")), 5000)
      ),
    ]);
    console.log("[Biometric] isAvailable result:", JSON.stringify(result));
    return result.isAvailable === true || (result.biometryType !== undefined && result.biometryType !== 0);
  } catch (e) {
    console.error("[Biometric] isAvailable error:", e);
    return false;
  }
}

/**
 * Returns the type label for the biometric method available (e.g. "Face ID", "Touch ID", "Fingerprint").
 */
export async function getBiometricTypeLabel(): Promise<string> {
  const loaded = await ensureBiometricPluginLoaded();
  const plugin = loaded ? getCachedBiometricPlugin() : null;
  if (!plugin) return "Biometrics";

  try {
    const { BiometryType } = await import("@capgo/capacitor-native-biometric");
    const result = await plugin.isAvailable();
    switch (result.biometryType) {
      case BiometryType.FACE_ID:
        return "Face ID";
      case BiometryType.TOUCH_ID:
        return "Touch ID";
      case BiometryType.FINGERPRINT:
        return "Fingerprint";
      case BiometryType.FACE_AUTHENTICATION:
        return "Face Unlock";
      default:
        return "Biometrics";
    }
  } catch {
    return "Biometrics";
  }
}

/**
 * Save credentials securely in the device keychain/keystore after biometric verification.
 */
export async function saveCredentials(email: string, password: string): Promise<boolean> {
  const loaded = await ensureBiometricPluginLoaded();
  const plugin = loaded ? getCachedBiometricPlugin() : null;
  if (!plugin) return false;

  try {
    await plugin.setCredentials({
      username: email,
      password: password,
      server: BIOMETRIC_SERVER,
    });
    localStorage.setItem(BIOMETRIC_OPT_IN_KEY, "true");
    console.log("[Biometric] Credentials saved");
    return true;
  } catch (e) {
    console.error("[Biometric] Failed to save credentials:", e);
    return false;
  }
}

/**
 * Check if biometric login has been enabled by the user previously.
 */
export function isBiometricOptedIn(): boolean {
  return localStorage.getItem(BIOMETRIC_OPT_IN_KEY) === "true";
}

/**
 * Suppress one automatic biometric prompt, typically after a manual logout.
 * Manual biometric button usage should still remain available.
 */
export function suppressBiometricAutoLogin(): void {
  try {
    sessionStorage.setItem(BIOMETRIC_AUTO_SUPPRESS_KEY, "true");
  } catch {
    // Ignore storage errors in restricted environments
  }
}

/**
 * Returns whether automatic biometric login was suppressed, and clears the flag.
 */
export function consumeBiometricAutoLoginSuppressed(): boolean {
  try {
    const suppressed = sessionStorage.getItem(BIOMETRIC_AUTO_SUPPRESS_KEY) === "true";
    if (suppressed) {
      sessionStorage.removeItem(BIOMETRIC_AUTO_SUPPRESS_KEY);
    }
    return suppressed;
  } catch {
    return false;
  }
}

/**
 * Prompt biometric verification and retrieve stored credentials.
 * Returns { email, password } on success, or null on failure/cancel.
 */
export async function authenticateWithBiometric(): Promise<{ email: string; password: string } | null> {
  console.log("[Biometric] authenticateWithBiometric called");
  const loaded = await ensureBiometricPluginLoaded();
  const plugin = loaded ? getCachedBiometricPlugin() : null;
  if (!plugin) {
    console.log("[Biometric] No plugin for authentication");
    return null;
  }

  try {
    console.log("[Biometric] Calling verifyIdentity...");
    await plugin.verifyIdentity({
      reason: "Log in to TransportMate",
      title: "TransportMate Login",
      subtitle: "Verify your identity",
      description: "Use biometrics to sign in quickly",
    });
    console.log("[Biometric] verifyIdentity succeeded, getting credentials...");

    const credentials = await plugin.getCredentials({ server: BIOMETRIC_SERVER });
    console.log("[Biometric] Credentials retrieved successfully");
    return { email: credentials.username, password: credentials.password };
  } catch (e) {
    console.log("[Biometric] Auth cancelled or failed:", e);
    return null;
  }
}

/**
 * Remove stored biometric credentials and opt-out.
 */
export async function clearBiometricCredentials(): Promise<void> {
  const loaded = await ensureBiometricPluginLoaded();
  const plugin = loaded ? getCachedBiometricPlugin() : null;

  if (plugin) {
    try {
      await plugin.deleteCredentials({ server: BIOMETRIC_SERVER });
    } catch (e) {
      console.warn("[Biometric] Failed to delete credentials:", e);
    }
  }

  localStorage.removeItem(BIOMETRIC_OPT_IN_KEY);
}
